import React, { useState } from 'react';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import { motion } from 'framer-motion';
import TuneIcon from '@mui/icons-material/Tune';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DescriptionIcon from '@mui/icons-material/Description';
import DataObjectIcon from '@mui/icons-material/DataObject';

const MotionCard = motion.create(Card);

const TYPE_COLORS = {
  compliance: 'primary',
  evaluation: 'warning',
  output: 'success',
  default: 'neutral',
};

const PolicyCard = ({
  policy,
  onEdit,
  onDuplicate,
  onDelete,
  index = 0,
}) => {
  const [hovered, setHovered] = useState(false);

  const {
    id,
    name,
    settings = {},
    policy: policyData = {},
    created_at,
  } = policy;

  const policyType = settings.type || 'Unknown';
  const description = policyData.description || '';
  const fieldsCount = policyData.documentFieldsSchema?.length || 0;
  const chipColor = TYPE_COLORS[policyType?.toLowerCase()] || TYPE_COLORS.default;

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Truncate description for display
  const displayDescription = description?.length > 80 
    ? `${description.substring(0, 80)}...` 
    : description;

  return (
    <MotionCard
      variant="outlined"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.3, 
        delay: index * 0.05,
        ease: 'easeOut',
      }}
      whileHover={{ 
        y: -4, 
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        transition: { duration: 0.2 },
      }}
      sx={{
        p: 2.5,
        cursor: 'pointer',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        minHeight: 180,
        transition: 'border-color 0.2s',
        '&:hover': {
          borderColor: 'primary.400',
        },
      }}
      onClick={() => onEdit(policy)}
    >
      {/* Action buttons - visible on hover */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 0.5,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.15s',
        }}
      >
        <IconButton
          size="sm"
          variant="soft"
          color="neutral"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(policy);
          }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="sm"
          variant="soft"
          color="neutral"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(policy);
          }}
        >
          <ContentCopyRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="sm"
          variant="soft"
          color="danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(policy);
          }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Header with icon and name */}
      <Stack direction="row" alignItems="flex-start" gap={1.5}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 'md',
            bgcolor: `${chipColor}.softBg`,
            color: `${chipColor}.600`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TuneIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            level="title-md"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: 6, // Space for action buttons
            }}
          >
            {name}
          </Typography>
          <Chip
            size="sm"
            variant="soft"
            color={chipColor}
            sx={{ mt: 0.5 }}
          >
            {policyType}
          </Chip>
        </Box>
      </Stack>

      {/* Description */}
      {displayDescription && (
        <Tooltip title={description} placement="bottom" enterDelay={300} sx={{ maxWidth: '20vw' }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            gap={0.75}
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 16, flexShrink: 0, mt: 0.25 }} />
            <Typography
              level="body-sm"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {displayDescription}
            </Typography>
          </Stack>
        </Tooltip>
      )}

      {/* Metadata row */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ mt: 'auto', pt: 1 }}
      >
        {fieldsCount > 0 && (
          <Tooltip title="Document fields">
            <Chip
              size="sm"
              variant="plain"
              color="neutral"
              startDecorator={<DataObjectIcon sx={{ fontSize: 14 }} />}
            >
              {fieldsCount} field{fieldsCount !== 1 ? 's' : ''}
            </Chip>
          </Tooltip>
        )}
        <Typography
          level="body-xs"
          sx={{
            color: 'text.tertiary',
            ml: 'auto',
          }}
        >
          ID: {id}
        </Typography>
      </Stack>

      {/* Footer with date */}
      {created_at && (
        <Typography
          level="body-xs"
          sx={{
            color: 'text.tertiary',
            mt: 0.5,
          }}
        >
          Created {formatDate(created_at)}
        </Typography>
      )}
    </MotionCard>
  );
};

export default PolicyCard;
